import Vue from 'vue';
import castPath from 'lodash/_castPath';
import isIndex from 'lodash/_isIndex';
import isObject from 'lodash/isObject';
import toKey from 'lodash/_toKey';
import last from 'lodash/last';
import nth from 'lodash/nth';
import parent from 'lodash/_parent';

export const uploadToS3 = async function (file, options = null) {
    const response = await axios.post(
        '/vapor/signed-storage-url',
        {
            bucket: options.bucket || '',
            content_type: options.contentType || file.type,
            expires: options.expires || '',
        },
        {
            baseURL: options.baseURL || null,
        },
    );

    let headers = response.data.headers;

    if ('Host' in headers) {
        delete headers.Host;
    }

    if (typeof options.progress === 'undefined') {
        options.progress = () => {};
    }

    const s3Response = await axios.put(response.data.url, file, {
        headers: headers,
        onUploadProgress: (progressEvent) => {
            options.progress(progressEvent.loaded / progressEvent.total);
        },
    });

    response.data.etag = _.get(s3Response, 'headers.etag', '').replace(/['"]+/g, '');
    response.data.extension = file.name.split('.').pop();

    return response.data;
};

/**
 * Binds all methods of a class instance to itself.
 */
export const autobind = function (instance) {
    for (let obj = instance; obj; obj = Object.getPrototypeOf(obj)) {
        // We're the end of the inheritance chain if we've reached 'Object'.
        if (obj.constructor === Object) {
            return;
        }

        let names = Object.getOwnPropertyNames(obj);

        // Bind each function to the instance.
        for (let i = 0; i < names.length; i++) {
            let name = names[i];

            // No need to bind getters, as attempting to access them would also
            // invoke them which is something we don't want to do here.
            if (Object.getOwnPropertyDescriptor(obj, name).get) {
                continue;
            }

            // We're using `defineProperty` here so that we don't make all the
            // class methods enumerable when we replace them.
            if (typeof obj[name] === 'function' && name !== 'constructor') {
                Object.defineProperty(instance, name, {
                    value: instance[name].bind(instance),
                    enumerable: false,
                    configurable: true,
                    writable: true,
                });
            }
        }
    }
};

/**
 * Exact copy of lodash set but replace assignValue with Vue.set
 *
 * @param object
 * @param path
 * @param value
 * @param customizer
 * @returns {*}
 */
export function baseSet(object, path, value, customizer) {
    if (!isObject(object)) {
        return object;
    }

    path = castPath(path, object);

    let index = -1,
        length = path.length,
        lastIndex = length - 1,
        nested = object;

    while (nested != null && ++index < length) {
        let key = toKey(path[index]),
            newValue = value;

        if (index != lastIndex) {
            let objValue = nested[key];
            newValue = customizer ? customizer(objValue, key, nested) : undefined;
            if (newValue === undefined) {
                newValue = isObject(objValue) ? objValue : isIndex(path[index + 1]) ? [] : {};
            }
        }
        Vue.set(nested, key, newValue);
        // assignValue(nested, key, newValue);
        nested = nested[key];
    }
    return object;
}

/**
 * Set attributes in object. Uses Vue.js
 *
 * @param object
 * @param path
 * @param value
 * @returns {*}
 */
export function set(object, path, value) {
    return object == null ? object : baseSet(object, path, value);
}

/**
 * The base implementation of `_.unset`.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {Array|string} path The property path to unset.
 * @returns {boolean} Returns `true` if the property is deleted, else `false`.
 */
function baseUnset(object, path) {
    path = castPath(path, object);
    let key = path.pop();
    object = parent(object, path);

    return object == null || Vue.delete(object[toKey(last(path))], key);
}

/**
 * Delete path from object. Uses Vue.js
 *
 * @param object
 * @param path
 * @param value
 * @returns {*}
 */
export function unset(object, path) {
    return object == null ? true : baseUnset(object, path);
}

/**
 * Convert bytes to human readable size.
 *
 * @param bytes
 * @param si
 * @returns {string}
 */
export function humanFileSize(bytes, si = true) {
    let thresh = si ? 1000 : 1024;
    if (Math.abs(bytes) < thresh) {
        return bytes + ' B';
    }
    let units = si ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    do {
        bytes /= thresh;
        ++u;
    } while (Math.abs(bytes) >= thresh && u < units.length - 1);
    return bytes.toFixed(1) + ' ' + units[u];
}

/**
 * Filter files based on file type.
 * @param newFile
 * @param oldFile
 * @param prevent
 * @returns {*}
 */
export function fileInputFilter(newFile, oldFile, prevent) {
    if (newFile && !oldFile) {
        if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
            return prevent();
        }
        if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
            return prevent();
        }
    }
}

/**
 * Get list of methods in object.
 * @param obj
 * @returns {string[]}
 */
export const getObjectMethods = (obj) => Object.getOwnPropertyNames(obj).filter((item) => typeof obj[item] === 'function');

/**
 * Plurlize a string.
 *
 * @param count
 * @param noun
 * @param suffix
 * @returns {string}
 */
export const pluralize = (count, noun, suffix = 's') => `${noun}${count !== 1 ? suffix : ''}`;

/**
 * Format numbers as money.
 *
 * @param value
 * @returns {string}
 */
export const money = (value) => {
    let symbol = null;
    let decimals = 0;
    let options = null;
    let thousandsSeparator, symbolOnLeft, spaceBetweenAmountAndSymbol;
    let digitsRE = /(\d{3})(?=\d)/g;
    options = options || {};
    value = parseFloat(value);
    if (!isFinite(value) || (!value && value !== 0)) return '';
    symbol = symbol != null ? symbol : '$';
    decimals = decimals != null ? decimals : 2;
    thousandsSeparator = options.thousandsSeparator != null ? options.thousandsSeparator : ',';
    symbolOnLeft = options.symbolOnLeft != null ? options.symbolOnLeft : true;
    spaceBetweenAmountAndSymbol = options.spaceBetweenAmountAndSymbol != null ? options.spaceBetweenAmountAndSymbol : false;
    let stringified = Math.abs(value).toFixed(decimals);
    stringified = options.decimalSeparator ? stringified.replace('.', options.decimalSeparator) : stringified;
    let _int = decimals ? stringified.slice(0, -1 - decimals) : stringified;
    let i = _int.length % 3;
    let head = i > 0 ? _int.slice(0, i) + (_int.length > 3 ? thousandsSeparator : '') : '';
    let _float = decimals ? stringified.slice(-1 - decimals) : '';
    symbol = spaceBetweenAmountAndSymbol ? (symbolOnLeft ? symbol + ' ' : ' ' + symbol) : symbol;
    symbol = symbolOnLeft
        ? symbol + head + _int.slice(i).replace(digitsRE, '$1' + thousandsSeparator) + _float
        : head + _int.slice(i).replace(digitsRE, '$1' + thousandsSeparator) + _float + symbol;
    let sign = value < 0 ? '-' : '';
    return sign + symbol;
};

export const isElement = (obj) => {
    try {
        return obj instanceof HTMLElement;
    } catch (e) {
        return typeof obj === 'object' && obj.nodeType === 1 && typeof obj.style === 'object' && typeof obj.ownerDocument === 'object';
    }
};

export const isVue = (obj) => {
    try {
        return obj instanceof Vue;
    } catch (e) {
        return typeof obj === 'object' && obj._isVue === true;
    }
};

export const resolveElement = (obj) => {
    if (isVue(obj)) {
        return [obj.$el];
    }

    if (isElement(obj)) {
        return [obj];
    }

    if (typeof obj === 'string') {
        let list = document.querySelectorAll(obj);
        return list ? list : null;
    }

    return null;
};

export const scrollToFirstError = () => {
    Vue.nextTick(function () {
        if ($('.invalid-feedback:visible').length) {
            $('html, body').animate(
                {
                    scrollTop: $('.invalid-feedback:visible:first').offset().top - 100,
                },
                500,
            );
        }
    });
};

export function randomString(length = 16) {
    return Math.round((Math.pow(36, length + 1) - Math.random() * Math.pow(36, length))).toString(36).slice(1);
}
